import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import HCaptchaField from "@/components/HCaptchaField";

export default function LeadForm({ propertyId = "", source = "website", compact = false }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", budget: "", purpose: "buy",
    timeline: "1-3 months", preferred_location: "Rewa", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    if (!user && !captchaToken) { toast.error("Please complete the captcha to continue"); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, property_id: propertyId, source, hcaptcha_token: user ? undefined : captchaToken });
      toast.success("Request received. Tharlo will contact you shortly.");
      setForm({ name: "", phone: "", email: "", budget: "", purpose: "buy", timeline: "1-3 months", preferred_location: "Rewa", message: "" });
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } catch (err) {
      captchaRef.current?.reset();
      setCaptchaToken(null);
      toast.error(err?.response?.data?.detail === "captcha_failed" ? "Please tick the 'I am human' checkbox above and try again." : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full bg-white border border-border focus:border-ink outline-none px-4 py-3 text-sm rounded-none transition-colors";

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-3">
      <div className={compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 md:grid-cols-2 gap-3"}>
        <input data-testid="lead-name" className={input} placeholder="Full name *" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} />
        <input data-testid="lead-phone" className={input} placeholder="Phone number *" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} />
        <input data-testid="lead-email" className={input} placeholder="Email (optional)" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} />
        <input data-testid="lead-budget" className={input} placeholder="Budget e.g. ₹50L - ₹1Cr" value={form.budget} onChange={(e)=>setForm({...form, budget:e.target.value})} />
        <select data-testid="lead-purpose" className={input} value={form.purpose} onChange={(e)=>setForm({...form, purpose:e.target.value})}>
          <option value="buy">Buy</option>
          <option value="rent">Rent</option>
          <option value="invest">Investment</option>
        </select>
        <select data-testid="lead-timeline" className={input} value={form.timeline} onChange={(e)=>setForm({...form, timeline:e.target.value})}>
          <option>Immediate</option>
          <option>1-3 months</option>
          <option>3-6 months</option>
          <option>6+ months</option>
        </select>
      </div>
      <input data-testid="lead-location" className={input} placeholder="Preferred location" value={form.preferred_location} onChange={(e)=>setForm({...form, preferred_location:e.target.value})} />
      <textarea data-testid="lead-message" className={input} rows={3} placeholder="Anything specific?" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} />
      <HCaptchaField ref={captchaRef} skip={!!user} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />
      <button type="submit" disabled={submitting} data-testid="lead-submit" className="btn-gold w-full">
        {submitting ? "Submitting..." : "Request Callback"}
      </button>
      <p className="text-[11px] text-muted-foreground">Your details are private and shared only with Tharlo's advisors.</p>
    </form>
  );
}
